import { useCallback, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { useToast } from '@/hooks/use-toast';
import { useBooking, useBookings } from '@/hooks/useBookings';
import { bookingsApi } from '@/lib/api/bookings.api';

import { BookingCalendar } from './components/BookingCalendar';
import { BookingDialog } from './components/BookingDialog';
import { BookingDrawer } from './components/BookingDrawer';
import { BookingFilters } from './components/BookingFilters';

import type { ExportPreset } from './components/BookingFilters';
import type { BookingWithRelations } from '@repo/shared-types';

const getExportDateRange = (preset: ExportPreset): { start?: string; end?: string } => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case 'all':
      return {};
    case 'today': {
      const endOfDay = new Date(today);
      endOfDay.setDate(endOfDay.getDate() + 1);
      return { start: today.toISOString(), end: endOfDay.toISOString() };
    }
    case 'week': {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 7);
      return { start: startOfWeek.toISOString(), end: endOfWeek.toISOString() };
    }
    case 'month': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      return { start: startOfMonth.toISOString(), end: endOfMonth.toISOString() };
    }
    case 'future':
      return { start: today.toISOString() };
    case 'past':
      return { end: today.toISOString() };
  }
};

const EXPORT_LABELS: Record<ExportPreset, string> = {
  all: 'all',
  today: 'today',
  week: 'this-week',
  month: 'this-month',
  future: 'future',
  past: 'past',
};

const BookingsPage = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get('search') || '';
  const campaignId = searchParams.get('campaignId') || 'all';
  const status = searchParams.get('status') || 'all';

  const updateParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (!value || value === 'all') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const rawStartDate = searchParams.get('startDate') || '';
  const rawEndDate = searchParams.get('endDate') || '';

  // FullCalendar's datesSet fires after the first render, so on initial load
  // the URL has no date params yet. Default to the current month so the API
  // returns the bookings visible in the calendar instead of the oldest 100.
  const now = new Date();
  const startDate = rawStartDate || new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const endDate = rawEndDate || new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();

  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState<BookingWithRelations | null>(null);

  const { data: bookingsData } = useBookings({
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    campaignId: campaignId !== 'all' ? campaignId : undefined,
    search: search || undefined,
    status: status !== 'all' ? status : undefined,
    limit: 1000,
  });

  const { data: selectedBooking } = useBooking(selectedBookingId ?? undefined);

  const handleDatesChange = useCallback(
    (start: string, end: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('startDate', start);
        next.set('endDate', end);
        return next;
      });
    },
    [setSearchParams],
  );

  const handleEventClick = useCallback((bookingId: string) => {
    setSelectedBookingId(bookingId);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setSelectedBookingId(null);
  }, []);

  const handleEdit = useCallback((booking: BookingWithRelations) => {
    setEditingBooking(booking);
    setDialogOpen(true);
    setSelectedBookingId(null);
  }, []);

  const handleAddClick = useCallback(() => {
    setEditingBooking(null);
    setDialogOpen(true);
  }, []);

  const handleExport = useCallback(
    async (preset: ExportPreset) => {
      try {
        const dateParams = getExportDateRange(preset);
        const filename = `bookings-${EXPORT_LABELS[preset]}.xlsx`;
        await bookingsApi.exportExcel(
          {
            startDate: dateParams.start,
            endDate: dateParams.end,
            campaignId: campaignId !== 'all' ? campaignId : undefined,
            search: search || undefined,
            status: status !== 'all' ? status : undefined,
            sortBy: 'date',
            sortOrder: 'asc',
          },
          filename,
        );
        toast({ title: 'Export downloaded' });
      } catch {
        toast({ title: 'Export failed', variant: 'destructive' });
      }
    },
    [campaignId, search, status, toast],
  );

  const bookings = bookingsData?.data ?? [];

  return (
    <div className="flex h-full flex-col">
      <BookingFilters
        search={search}
        onSearchChange={(v) => updateParam('search', v)}
        campaignId={campaignId}
        onCampaignChange={(v) => updateParam('campaignId', v)}
        status={status}
        onStatusChange={(v) => updateParam('status', v)}
        onAddClick={handleAddClick}
        onExportClick={handleExport}
      />
      <BookingCalendar
        bookings={bookings}
        initialDate={startDate || undefined}
        onEventClick={handleEventClick}
        onDatesChange={handleDatesChange}
      />
      <BookingDrawer
        booking={selectedBooking ?? null}
        onClose={handleCloseDrawer}
        onEdit={handleEdit}
      />
      <BookingDialog open={dialogOpen} onOpenChange={setDialogOpen} booking={editingBooking} />
    </div>
  );
};

export default BookingsPage;
